import React, { useState, useEffect } from 'react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { formatDate } from '../patients/utils/patientUtils';

interface DiagnosisCategory {
  id: string;
  name: string;
  specialty?: string;
}

interface DiagnosisSearchResult {
  id: string;
  name: string;
  icd_10_code?: string;
  category_name?: string;
  is_chronic?: boolean;
}

interface PatientDiagnosis {
  id: string;
  patient_id: string;
  diagnosis_template: string;
  diagnosis_name?: string;
  diagnosis_icd_10?: string;
  category_name?: string;
  onset_date?: string;
  diagnosis_date: string;
  severity?: string;
  status: string;
  notes?: string;
  diagnosed_by: string;
  diagnosed_by_name?: string;
  is_primary: boolean;
  consultation_id?: string | null;
}

interface FormDataState {
  diagnosis_date: string;
  onset_date: string;
  severity: string;
  status: string;
  notes: string;
  is_primary: boolean;
}

const EMPTY_FORM_DATA: FormDataState = {
  diagnosis_date: new Date().toISOString().split('T')[0],
  onset_date: '',
  severity: '',
  status: 'Active',
  notes: '',
  is_primary: false
};

interface AddDiagnosisModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patient?: { first_name: string; last_name: string; date_of_birth?: string } | null;
  onDiagnosisAdded: (diagnosis: PatientDiagnosis) => void;
  // Set when opened from an active consult, so the diagnosis is linked back
  // to it (see backend/modules/clinical/models.py PatientDiagnosis.
  // consultation_id). Omit when adding directly to a patient's file outside
  // any consultation - that stays fully supported, consultation_id is
  // optional throughout.
  consultationId?: string | null;
}

const AddDiagnosisModal: React.FC<AddDiagnosisModalProps> = ({ isOpen, onClose, patientId, patient, onDiagnosisAdded, consultationId }) => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<DiagnosisSearchResult[]>([]);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<DiagnosisSearchResult | null>(null);
  const [categories, setCategories] = useState<DiagnosisCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState<FormDataState>(EMPTY_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && token) {
      fetchCategories();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (searchTerm.length >= 2 && token) {
      searchDiagnoses();
    } else {
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, token]);

  const fetchCategories = async () => {
    if (!token) return;
    try {
      const response = await apiCall('/clinical/diagnosis-categories/', {
        method: 'GET'
      }, token);

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const searchDiagnoses = async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams({
        q: searchTerm,
        ...(selectedCategory && { category: selectedCategory })
      });

      const response = await apiCall(`/clinical/diagnosis-templates/search/?${params}`, {
        method: 'GET'
      }, token);

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching diagnoses:', error);
    }
  };

  const handleDiagnosisSelect = (diagnosis: DiagnosisSearchResult) => {
    setSelectedDiagnosis(diagnosis);
    setSearchTerm(diagnosis.name);
    setSearchResults([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDiagnosis) {
      setError('Please select a diagnosis');
      return;
    }

    if (!token) {
      setError('Authentication required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await apiCall('/clinical/patients/diagnoses/create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_id: patientId,
          diagnosis_template: selectedDiagnosis.id,
          consultation_id: consultationId || null,
          ...formData
        }),
      }, token);

      if (response.ok) {
        const newDiagnosis = await response.json();
        onDiagnosisAdded(newDiagnosis);
        handleClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to add diagnosis');
      }
    } catch (error) {
      setError('Failed to add diagnosis');
      console.error('Error adding diagnosis:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedDiagnosis(null);
    setSelectedCategory('');
    setFormData(EMPTY_FORM_DATA);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <h2 className="text-xl font-semibold text-base-content">
            Add New Diagnosis for {patient ? `${patient.first_name} ${patient.last_name}` : 'Patient'}
            {patient?.date_of_birth && ` (DOB: ${formatDate(patient.date_of_birth)})`}
          </h2>
          <button
            onClick={handleClose}
            className="text-base-content opacity-60 hover:opacity-100 transition-colors"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Filter by Category (Optional)
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="select select-bordered w-full"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} {category.specialty && `(${category.specialty})`}
                </option>
              ))}
            </select>
          </div>

          {/* Diagnosis Search */}
          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Search for Diagnosis *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-base-content opacity-50" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input input-bordered w-full pl-10"
                placeholder="Type to search diagnoses..."
                required
              />
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 border border-base-300 rounded-md max-h-48 overflow-y-auto bg-base-100">
                {searchResults.map((diagnosis) => (
                  <button
                    key={diagnosis.id}
                    type="button"
                    onClick={() => handleDiagnosisSelect(diagnosis)}
                    className="w-full text-left px-4 py-2 hover:bg-base-200 border-b border-base-200 last:border-b-0"
                  >
                    <div className="font-medium text-base-content">{diagnosis.name}</div>
                    <div className="text-sm text-base-content opacity-60 flex items-center gap-2">
                      {diagnosis.icd_10_code && (
                        <span>ICD-10: {diagnosis.icd_10_code}</span>
                      )}
                      <span>{diagnosis.category_name}</span>
                      {diagnosis.is_chronic && (
                        <span className="badge badge-info badge-sm">
                          Chronic
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Selected Diagnosis */}
            {selectedDiagnosis && (
              <div className="mt-2 p-3 bg-info/10 border border-info rounded-md">
                <div className="font-medium text-info">{selectedDiagnosis.name}</div>
                <div className="text-sm text-info opacity-80">
                  {selectedDiagnosis.icd_10_code && `ICD-10: ${selectedDiagnosis.icd_10_code} • `}
                  {selectedDiagnosis.category_name}
                </div>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Diagnosis Date *
              </label>
              <input
                type="date"
                value={formData.diagnosis_date}
                onChange={(e) => setFormData({ ...formData, diagnosis_date: e.target.value })}
                className="input input-bordered w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Onset Date
              </label>
              <input
                type="date"
                value={formData.onset_date}
                onChange={(e) => setFormData({ ...formData, onset_date: e.target.value })}
                className="input input-bordered w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Severity
              </label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="select select-bordered w-full"
              >
                <option value="">Select severity...</option>
                <option value="Mild">Mild</option>
                <option value="Moderate">Moderate</option>
                <option value="Severe">Severe</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="select select-bordered w-full"
              >
                <option value="Active">Active</option>
                <option value="Resolved">Resolved</option>
                <option value="In Remission">In Remission</option>
              </select>
            </div>
          </div>

          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_primary}
                onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                className="checkbox checkbox-primary"
              />
              <span className="ml-2 text-sm font-medium text-base-content">
                Primary Diagnosis
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="textarea textarea-bordered w-full"
              placeholder="Additional notes about this diagnosis..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-ghost flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedDiagnosis}
              className="btn btn-primary flex-1"
            >
              {isSubmitting ? 'Adding...' : 'Add Diagnosis'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDiagnosisModal;
